/**
 * SystemPill — the topbar's host/freshness status pill (Wave 2c).
 *
 * Purely presentational: props in, JSX out. No fetch, no polling, no global
 * reads. The App owns both polls and passes the decoded {@link HostStatus} and
 * the computed {@link Freshness} line down. The pill is calm by default — it
 * names the host state and the data age, and only the freshness half goes loud
 * (`disconnected — retrying`) when polling drops.
 */

import type { HostStatus } from './board-host'
import type { Freshness } from './format'
import { type HostHeadline, formatPercent, formatRamMb } from './metrics'

export interface SystemPillProps {
  /** Latest decoded host status, or null before the first `/status` lands. */
  host: HostStatus | null
  /**
   * The CPU% + free-RAM headline from `/metrics`, or null before the first
   * sample lands. Folded into the pill so the topbar leads with how busy the box
   * is, not just its readiness state. A null gauge renders an em dash.
   */
  headline?: HostHeadline | null
  /** The honest freshness line for the most recent successful poll. */
  fresh: Freshness
}

export function SystemPill({ host, headline, fresh }: SystemPillProps): JSX.Element {
  const state = host?.state ?? 'connecting'
  return (
    <div className="system-pill" data-testid="system-pill" data-state={state}>
      <span className="system-pill-dot" aria-hidden="true" />
      <span className="system-pill-label">host: {state}</span>
      {headline ? (
        <span className="system-pill-headline tabular" data-testid="system-pill-headline">
          cpu {formatPercent(headline.cpuPercent)} · mem {formatRamMb(headline.freeRamMb)}
        </span>
      ) : null}
      <span
        className={`system-pill-fresh tabular${fresh.disconnected ? ' is-disconnected' : ''}`}
        data-testid="system-pill-fresh"
        data-disconnected={fresh.disconnected}
      >
        {fresh.text}
      </span>
    </div>
  )
}
